<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in-0 duration-300"
  >
    <div
      class="bg-white w-full sm:max-w-5xl max-h-screen sm:max-h-[95vh] sm:rounded-xl shadow-xl relative flex flex-col overflow-hidden animate-in zoom-in-95 duration-300"
    >
      <!-- Header -->
      <div
        class="flex-shrink-0 bg-gradient-to-r from-slate-100 to-gray-100 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between"
      >
        <div class="flex items-center gap-3 flex-1 min-w-0">
          <div class="p-2 bg-blue-100 rounded-lg">
            <FileText class="w-5 h-5 text-blue-600" />
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="text-lg font-semibold text-gray-800 truncate">
              {{ documentTitle }}
            </h3>
            <p class="text-sm text-gray-500">Document Viewer</p>
          </div>
        </div>
        <button
          @click="$emit('close')"
          class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-105"
          title="Close"
        >
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-hidden bg-gray-50">
        <div
          v-if="documentUrl"
          class="w-full h-full"
        >
          <!-- PDF -->
          <iframe
            v-if="
              documentUrl.includes('.pdf') ||
              documentTitle.toLowerCase().includes('pdf')
            "
            :src="`https://docs.google.com/gview?url=${encodeURIComponent(
              documentUrl
            )}&embedded=true`"
            class="w-full h-full border-0 bg-white shadow-inner"
            title="PDF Viewer"
          ></iframe>

          <!-- Image -->
          <div
            v-else
            class="w-full h-full flex items-center justify-center p-6"
          >
            <div
              class="max-w-full max-h-full bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <img
                :src="documentUrl"
                :alt="documentTitle"
                class="max-w-full max-h-full object-contain"
              />
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div
          v-else
          class="w-full h-full flex items-center justify-center"
        >
          <div class="text-center">
            <div class="relative">
              <div
                class="w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"
              ></div>
              <div
                class="absolute inset-0 w-12 h-12 border-4 border-transparent border-r-blue-300 rounded-full animate-spin mx-auto"
                style="animation-direction: reverse; animation-duration: 1.5s"
              ></div>
            </div>
            <p class="text-base font-medium text-gray-700 mb-1">
              Loading document...
            </p>
            <p class="text-sm text-gray-500">
              Please wait while we prepare your document
            </p>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex-shrink-0 bg-gradient-to-r from-slate-100 to-gray-100 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3"
      >
        <div class="flex items-center gap-2 text-sm text-gray-600">
          <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span>Document loaded successfully</span>
        </div>
        <div class="flex gap-3 w-full sm:w-auto">
          <button
            @click="$emit('close')"
            class="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 hover:border-gray-400 transition duration-200 hover:scale-105"
          >
            Close
          </button>
          <button
            @click="downloadFile"
            class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white font-medium rounded-lg hover:from-green-700 hover:to-green-800 transition duration-200 hover:scale-105 shadow hover:shadow-lg"
          >
            <Download class="w-4 h-4" />
            <span>Download</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { X, FileText, Download } from "lucide-vue-next";

  const props = defineProps({
    show: Boolean,
    documentUrl: String,
    documentTitle: String,
  });

  defineEmits(["close"]);

  function downloadFile() {
    if (!props.documentUrl) return;

    let downloadUrl = props.documentUrl;
    if (downloadUrl.includes("/upload/")) {
      downloadUrl = downloadUrl.replace("/upload/", "/upload/fl_attachment/");
    }

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute(
      "download",
      props.documentTitle.toLowerCase().replace(/\s+/g, "-")
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
</script>

<style scoped>
  @keyframes fade-in-0 {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes zoom-in-95 {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .animate-in {
    animation-fill-mode: both;
  }

  .fade-in-0 {
    animation-name: fade-in-0;
  }

  .zoom-in-95 {
    animation-name: zoom-in-95;
  }

  .duration-300 {
    animation-duration: 300ms;
  }

  .touch-manipulation {
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
</style>
